import axios from 'axios';
import { config } from '../../../config';
import { createSubscriptionFromData, checkSubscriptionWarnings } from './SubscriptionModel';

class SubscriptionService {
    constructor() {
        this.apiBase = config.API_BASE;
    }

    // Get user's current subscription
    async getUserSubscription(userId, token) {
        try {
            // Add null checks for userId and token
            if (!userId || !token) {
                console.warn('getUserSubscription: Missing userId or token');
                return { subscription: null, warnings: [] };
            }
            
            const response = await axios.get(`${this.apiBase}/api/subscriptions/user/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data && response.data.subscription) {
                try {
                    const subscription = createSubscriptionFromData(response.data.subscription);
                    const warnings = checkSubscriptionWarnings(subscription);
                    return { subscription, warnings };
                } catch (subscriptionError) {
                    console.error('Error creating subscription object:', subscriptionError);
                    return { subscription: null, warnings: [] };
                }
            }
            
            return { subscription: null, warnings: [] };
        } catch (error) {
            console.error('Error fetching user subscription:', error);
            return { subscription: null, warnings: [] };
        }
    }

    // Start trial subscription
    async startTrial(userId, token) {
        try {
            // Add null checks for userId and token
            if (!userId || !token) {
                console.warn('startTrial: Missing userId or token');
                return { success: false, error: 'Missing user ID or token' };
            }
            
            const response = await axios.post(`${this.apiBase}/api/subscriptions/start-trial`, {
                userId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data && response.data.success) {
                try {
                    const subscription = createSubscriptionFromData(response.data.subscription);
                    return { success: true, subscription };
                } catch (subscriptionError) {
                    console.error('Error creating subscription object:', subscriptionError);
                    return { success: false, error: 'Invalid subscription data received' };
                }
            }
            
            return { success: false, error: response.data?.error || 'Failed to start trial' };
        } catch (error) {
            console.error('Error starting trial:', error);
            return { 
                success: false, 
                error: error.response?.data?.error || 'Failed to start trial' 
            };
        }
    }

    // Migrate trial user to Basic plan
    async migrateTrialToBasic(userId, token) {
        try {
            if (!userId || !token) {
                console.warn('migrateTrialToBasic: Missing userId or token');
                return { success: false, error: 'Missing user ID or token' };
            }
            
            const response = await axios.post(`${this.apiBase}/api/subscriptions/migrate-trial-to-basic`, {
                userId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data && response.data.success) {
                try {
                    const subscription = createSubscriptionFromData(response.data.subscription);
                    return { success: true, subscription };
                } catch (subscriptionError) {
                    console.error('Error creating subscription object:', subscriptionError);
                    return { success: false, error: 'Invalid subscription data received' };
                }
            }
            
            return { success: false, error: response.data?.error || 'Failed to migrate to Basic plan' };
        } catch (error) {
            console.error('Error migrating to Basic plan:', error);
            return { 
                success: false, 
                error: error.response?.data?.error || 'Failed to migrate to Basic plan' 
            };
        }
    }

    // Upgrade to Premium Plan
    async upgrade(userId, token) {
        try {
            if (!userId || !token) {
                console.warn('upgrade: Missing userId or token');
                return { success: false, error: 'Missing user ID or token' };
            }

            const response = await axios.post(`${this.apiBase}/api/subscriptions/upgrade`, {
                userId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                try {
                    const subscription = createSubscriptionFromData(response.data.subscription);
                    return { success: true, subscription };
                } catch (subscriptionError) {
                    console.error('Error creating subscription object:', subscriptionError);
                    return { success: false, error: 'Invalid subscription data received' };
                }
            }

            return { success: false, error: response.data?.error || 'Failed to upgrade to Premium' };
        } catch (error) {
            console.error('Error upgrading to Premium:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Failed to upgrade to Premium'
            };
        }
    }

    // Cancel Membership
    async cancel(userId, token) {
        try {
            if (!userId || !token) {
                console.warn('cancel: Missing userId or token');
                return { success: false, error: 'Missing user ID or token' };
            }

            const response = await axios.post(`${this.apiBase}/api/subscriptions/cancel`, {
                userId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                try {
                    const subscription = createSubscriptionFromData(response.data.subscription);
                    return {
                        success: true,
                        subscription,
                        message: response.data.message
                    };
                } catch (subscriptionError) {
                    console.error('Error creating subscription object:', subscriptionError);
                    return { success: false, error: 'Invalid subscription data received' };
                }
            }

            return { success: false, error: response.data?.error || 'Failed to cancel membership' };
        } catch (error) {
            console.error('Error cancelling membership:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Failed to cancel membership'
            };
        }
    }

    // Reactivate Membership (Re-enable auto-renewal)
    async reactivate(userId, token) {
        try {
            if (!userId || !token) {
                console.warn('reactivate: Missing userId or token');
                return { success: false, error: 'Missing user ID or token' };
            }

            const response = await axios.post(`${this.apiBase}/api/subscriptions/reactivate`, {
                userId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                try {
                    const subscription = createSubscriptionFromData(response.data.subscription);
                    return {
                        success: true,
                        subscription,
                        message: response.data.message
                    };
                } catch (subscriptionError) {
                    console.error('Error creating subscription object:', subscriptionError);
                    return { success: false, error: 'Invalid subscription data received' };
                }
            }

            return { success: false, error: response.data?.error || 'Failed to reactivate membership' };
        } catch (error) {
            console.error('Error reactivating membership:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Failed to reactivate membership'
            };
        }
    }

    // Change Premium tier (switch from one pricing tier to another)
    async changeTier(userId, newPriceId, token) {
        try {
            if (!userId || !newPriceId || !token) {
                console.warn('changeTier: Missing userId, newPriceId, or token');
                return { success: false, error: 'Missing required parameters' };
            }

            const response = await axios.post(`${this.apiBase}/api/subscriptions/change-tier`, {
                userId,
                newPriceId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                try {
                    const subscription = createSubscriptionFromData(response.data.subscription);
                    return {
                        success: true,
                        subscription,
                        message: response.data.message
                    };
                } catch (subscriptionError) {
                    console.error('Error creating subscription object:', subscriptionError);
                    return { success: false, error: 'Invalid subscription data received' };
                }
            }

            return { success: false, error: response.data?.error || 'Failed to change tier' };
        } catch (error) {
            console.error('Error changing tier:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Failed to change tier'
            };
        }
    }

    // Create premium subscription SetupIntent for embedded payment
    async createPremiumPaymentIntent(userId, token, priceId = null, isChangingTier = false) {
        try {
            const requestBody = {
                userId,
                planId: 'premium'
            };

            // Include priceId if provided (for multi-tier pricing)
            if (priceId) {
                requestBody.priceId = priceId;
            }

            // Include isChangingTier flag to allow tier changes for existing subscribers
            if (isChangingTier) {
                requestBody.isChangingTier = true;
            }

            const response = await axios.post(`${this.apiBase}/api/subscriptions/create-subscription-intent`, requestBody, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.clientSecret) {
                return {
                    success: true,
                    clientSecret: response.data.clientSecret,
                    amount: response.data.amount,
                    currency: response.data.currency,
                    setupIntentId: response.data.setupIntentId,
                    period: response.data.period
                };
            }

            return { success: false, error: 'Failed to create setup intent' };
        } catch (error) {
            console.error('Error creating subscription setup intent:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to create setup intent'
            };
        }
    }

    // Confirm premium subscription payment with SetupIntent
    async confirmPremiumSubscription(userId, setupIntentId, token) {
        try {
            const response = await axios.post(`${this.apiBase}/api/subscriptions/confirm-subscription`, {
                userId,
                planId: 'premium',
                setupIntentId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                return {
                    success: true,
                    subscription: response.data.subscription,
                    message: response.data.message
                };
            }

            return { success: false, error: response.data.message || 'Failed to confirm subscription' };
        } catch (error) {
            console.error('Error confirming subscription:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to confirm subscription'
            };
        }
    }

    // Create premium subscription checkout session (DEPRECATED - keeping for backward compatibility)
    async createPremiumCheckout(userId, token) {
        try {
            const response = await axios.post(`${this.apiBase}/api/subscriptions/create-checkout-session`, {
                userId,
                planId: 'premium'
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.url) {
                return { success: true, checkoutUrl: response.data.url };
            }
            
            return { success: false, error: 'Failed to create checkout session' };
        } catch (error) {
            console.error('Error creating checkout session:', error);
            return { 
                success: false, 
                error: error.response?.data?.error || 'Failed to create checkout session' 
            };
        }
    }

    // Cancel subscription
    async cancelSubscription(subscriptionId, token) {
        try {
            const response = await axios.post(`${this.apiBase}/api/subscriptions/${subscriptionId}/cancel`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                const subscription = createSubscriptionFromData(response.data.subscription);
                return { success: true, subscription };
            }
            
            return { success: false, error: response.data.error || 'Failed to cancel subscription' };
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            return { 
                success: false, 
                error: error.response?.data?.error || 'Failed to cancel subscription' 
            };
        }
    }

    // Reactivate subscription
    async reactivateSubscription(subscriptionId, token) {
        try {
            const response = await axios.post(`${this.apiBase}/api/subscriptions/${subscriptionId}/reactivate`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                const subscription = createSubscriptionFromData(response.data.subscription);
                return { success: true, subscription };
            }
            
            return { success: false, error: response.data.error || 'Failed to reactivate subscription' };
        } catch (error) {
            console.error('Error reactivating subscription:', error);
            return { 
                success: false, 
                error: error.response?.data?.error || 'Failed to reactivate subscription' 
            };
        }
    }

    // Get subscription history
    async getSubscriptionHistory(userId, token) {
        try {
            if (!userId || !token) {
                console.warn('getSubscriptionHistory: Missing userId or token');
                return { success: false, subscriptions: [] };
            }

            const response = await axios.get(`${this.apiBase}/api/subscriptions/user/${userId}/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.subscriptions) {
                return {
                    success: true,
                    subscriptions: response.data.subscriptions
                };
            }

            return { success: false, subscriptions: [] };
        } catch (error) {
            console.error('Error fetching subscription history:', error);
            return { success: false, subscriptions: [] };
        }
    }

    // Get transactions (Stripe invoices) for a specific subscription
    async getSubscriptionTransactions(subscriptionId, token) {
        try {
            if (!subscriptionId || !token) {
                console.warn('getSubscriptionTransactions: Missing subscriptionId or token');
                return { success: false, invoices: [], events: [] };
            }

            const response = await axios.get(`${this.apiBase}/api/subscriptions/subscription/${subscriptionId}/transactions`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            return {
                success: true,
                invoices: response.data.invoices || [],
                events: response.data.events || []
            };
        } catch (error) {
            console.error('Error fetching subscription transactions:', error);
            return { success: false, invoices: [], events: [] };
        }
    }

    // Update subscription preferences
    async updateSubscriptionPreferences(subscriptionId, preferences, token) {
        try {
            const response = await axios.put(`${this.apiBase}/api/subscriptions/${subscriptionId}/preferences`, preferences, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                const subscription = createSubscriptionFromData(response.data.subscription);
                return { success: true, subscription };
            }
            
            return { success: false, error: response.data.error || 'Failed to update preferences' };
        } catch (error) {
            console.error('Error updating subscription preferences:', error);
            return { 
                success: false, 
                error: error.response?.data?.error || 'Failed to update preferences' 
            };
        }
    }

    // Check for subscription warnings (for notifications)
    async checkWarnings(userId, token) {
        try {
            // Add null checks for userId and token
            if (!userId || !token) {
                console.warn('checkWarnings: Missing userId or token');
                return [];
            }

            // getUserSubscription already calls checkSubscriptionWarnings internally
            // No need to call it again to avoid duplicate warnings
            const { subscription, warnings } = await this.getUserSubscription(userId, token);

            return warnings || [];
        } catch (error) {
            console.error('Error checking subscription warnings:', error);
            return [];
        }
    }

    // Get premium plan pricing from Stripe (no auth required) - single price (backward compatible)
    async getPremiumPrice() {
        try {
            const response = await axios.get(`${this.apiBase}/api/subscriptions/premium-price`);

            if (response.data) {
                return {
                    success: true,
                    price: response.data.price,
                    currency: response.data.currency,
                    interval: response.data.interval,
                    formatted: response.data.formatted,
                    period: response.data.period
                };
            }

            return { success: false, error: 'Failed to fetch premium price' };
        } catch (error) {
            console.error('Error fetching premium price:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to fetch premium price'
            };
        }
    }

    // Get all premium plan pricing tiers from Stripe (no auth required)
    async getPremiumPrices() {
        try {
            const response = await axios.get(`${this.apiBase}/api/subscriptions/premium-prices`);

            if (response.data && response.data.prices) {
                return {
                    success: true,
                    prices: response.data.prices,
                    defaultPriceId: response.data.defaultPriceId
                };
            }

            return { success: false, error: 'Failed to fetch premium prices' };
        } catch (error) {
            console.error('Error fetching premium prices:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to fetch premium prices'
            };
        }
    }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
